import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { alCerrarseLaSesion, api, guardarToken } from '../api/cliente';
import type { RespuestaSesion, UsuarioSesion } from '../api/tipos';

interface Sesion {
  usuario: UsuarioSesion | null;
  cargando: boolean;
  entrar: (email: string, password: string) => Promise<void>;
  salir: () => Promise<void>;
  /** `inmueble:editar`, `configuracion:editar`… */
  puede: (permiso: string) => boolean;
}

const Contexto = createContext<Sesion | null>(null);

/**
 * Quién entró y qué puede hacer.
 *
 * Al arrancar intenta un refresco con la cookie: si la sesión seguía viva,
 * recargar la página no obliga a volver a escribir la contraseña. El token de
 * acceso vive solo quince minutos y en memoria, así que sin este paso cada F5
 * sería un login.
 */
export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    alCerrarseLaSesion(() => setUsuario(null));

    let vigente = true;
    (async () => {
      try {
        const respuesta = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          headers: { 'X-Refresh': '1' },
          credentials: 'include',
        });
        // 204: no habia cookie, es la primera visita. 401: habia algo y no
        // servia. Las dos acaban igual aqui —sin sesion— pero solo la segunda
        // es un fallo.
        if (respuesta.status === 204 || !respuesta.ok) return;
        const datos = (await respuesta.json()) as RespuestaSesion;
        guardarToken(datos.accessToken);
        const yo = await api<UsuarioSesion>('/auth/yo');
        if (vigente) setUsuario(yo);
      } catch {
        // Sin sesión previa. Es el caso normal la primera vez.
      } finally {
        if (vigente) setCargando(false);
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  const entrar = useCallback(async (email: string, password: string) => {
    const datos = await api<RespuestaSesion>('/auth/login', {
      metodo: 'POST',
      cuerpo: { email, password },
    });
    guardarToken(datos.accessToken);
    setUsuario(datos.usuario);
  }, []);

  const salir = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { metodo: 'POST' });
    } finally {
      // Aunque el servidor no conteste, aquí dentro la sesión terminó.
      guardarToken(null);
      setUsuario(null);
    }
  }, []);

  const valor = useMemo<Sesion>(() => {
    const permisos = new Set(usuario?.permisos ?? []);
    return {
      usuario,
      cargando,
      entrar,
      salir,
      puede: (permiso) => permisos.has(permiso),
    };
  }, [usuario, cargando, entrar, salir]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const sesion = useContext(Contexto);
  if (!sesion) throw new Error('useSesion fuera del proveedor');
  return sesion;
}
